import React, { useEffect, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  Cell,
  PieChart,
  Pie,
  Legend,
} from 'recharts';

// ── colour tokens (mirrors CSS vars) ─────────────────────────────────────────
const C = {
  accent:    '#6366f1',
  success:   '#22c55e',
  warning:   '#f59e0b',
  danger:    '#ef4444',
  muted:     '#64748b',
  surface:   '#1e293b',
  elevated:  '#273549',
  border:    '#334155',
  textPrimary:   '#f1f5f9',
  textSecondary: '#94a3b8',
};

// ── helpers ───────────────────────────────────────────────────────────────────

function ErrorBanner({ message }) {
  return (
    <div style={{
      background: 'rgba(239,68,68,0.1)',
      border: `1px solid ${C.danger}`,
      borderRadius: 8,
      padding: '12px 16px',
      color: C.danger,
      fontSize: 13,
    }}>
      {message}
    </div>
  );
}

function Spinner() {
  return (
    <div style={{ display: 'flex', justifyContent: 'center', padding: 48 }}>
      <div style={{
        width: 32, height: 32,
        border: `3px solid ${C.border}`,
        borderTopColor: C.accent,
        borderRadius: '50%',
        animation: 'spin 0.7s linear infinite',
      }} />
      <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
    </div>
  );
}

// Custom tooltip for the Products bar chart
function ProductTooltip({ active, payload }) {
  if (!active || !payload?.length) return null;
  const d = payload[0].payload;
  return (
    <div style={{
      background: C.elevated,
      border: `1px solid ${C.border}`,
      borderRadius: 8,
      padding: '10px 14px',
      fontSize: 13,
      maxWidth: 260,
    }}>
      <div style={{ fontWeight: 600, marginBottom: 6, color: C.textPrimary }}>{d.name}</div>
      <div style={{ color: C.textSecondary }}>
        Features: <span style={{ color: C.textPrimary, fontWeight: 500 }}>{d.feature_count}</span>
      </div>
      {d.pm_names ? (
        <div style={{ color: C.textSecondary, marginTop: 4 }}>
          PMs: <span style={{ color: C.textPrimary }}>{d.pm_names}</span>
        </div>
      ) : (
        <div style={{ color: C.muted, marginTop: 4, fontStyle: 'italic' }}>No PMs assigned</div>
      )}
    </div>
  );
}

// Custom tooltip for the repo mapping donut
function MappingTooltip({ active, payload }) {
  if (!active || !payload?.length) return null;
  const d = payload[0];
  return (
    <div style={{
      background: C.elevated,
      border: `1px solid ${C.border}`,
      borderRadius: 8,
      padding: '10px 14px',
      fontSize: 13,
    }}>
      <div style={{ fontWeight: 600, color: d.payload.fill }}>{d.name}</div>
      <div style={{ color: C.textSecondary, marginTop: 4 }}>
        {d.value} features ({d.payload.pct}%)
      </div>
    </div>
  );
}

// Stat card
function StatCard({ label, value, sub, tone }) {
  const toneColor = tone === 'success' ? C.success
                  : tone === 'warning' ? C.warning
                  : tone === 'danger'  ? C.danger
                  : C.accent;
  return (
    <div style={{
      background: C.surface,
      border: `1px solid ${C.border}`,
      borderRadius: 10,
      padding: '16px 20px',
    }}>
      <div style={{ fontSize: 12, color: C.textSecondary, textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: 6 }}>
        {label}
      </div>
      <div style={{ fontSize: 28, fontWeight: 700, color: toneColor, lineHeight: 1 }}>
        {value}
      </div>
      {sub && <div style={{ fontSize: 12, color: C.muted, marginTop: 6 }}>{sub}</div>}
    </div>
  );
}

// Truncate long product names for the x-axis tick (rotated to prevent overlap)
function TruncatedTick({ x, y, payload }) {
  const label = payload.value.length > 18 ? payload.value.slice(0, 17) + '…' : payload.value;
  return (
    <text
      x={x}
      y={y + 6}
      textAnchor="end"
      fill={C.textSecondary}
      fontSize={11}
      transform={`rotate(-40, ${x}, ${y + 6})`}
    >
      {label}
    </text>
  );
}

// ── Coverage helpers ──────────────────────────────────────────────────────────

function StaleBanner({ staleRepos, navigate }) {
  if (!staleRepos || staleRepos.length === 0) return null;
  const names = staleRepos.map(r => r.repo_name).join(', ');
  return (
    <div style={{
      display: 'flex',
      alignItems: 'flex-start',
      gap: 10,
      background: 'rgba(245,158,11,0.10)',
      border: `1px solid ${C.warning}`,
      borderRadius: 8,
      padding: '10px 14px',
      fontSize: 13,
      color: C.warning,
      margin: '12px 0 0',
    }}>
      <span style={{ fontSize: 15, lineHeight: 1.4, flexShrink: 0 }}>&#9888;</span>
      <div>
        <strong>Coverage data is outdated</strong> for: <span style={{ fontStyle: 'italic' }}>{names}</span>.
        Features were modified since the last report was generated.{' '}
        <button
          onClick={() => navigate('/jobs')}
          style={{
            background: 'none', border: 'none', color: C.warning,
            cursor: 'pointer', fontSize: 13, padding: 0,
            textDecoration: 'underline', fontWeight: 600,
          }}
        >
          Run the Coverage Report job
        </button>{' '}
        to refresh.
      </div>
    </div>
  );
}

function CoverageBadge({ pct }) {
  const color = pct >= 80 ? C.success : pct >= 50 ? C.warning : C.danger;
  return (
    <span style={{
      color,
      fontWeight: 600,
      background: `${color}18`,
      padding: '2px 8px',
      borderRadius: 12,
      fontSize: 12,
      whiteSpace: 'nowrap',
    }}>
      {pct}%
    </span>
  );
}

function ProviderBadge({ provider }) {
  const map = {
    github:    { bg: 'rgba(99,102,241,0.15)', fg: '#818cf8' },
    gitlab:    { bg: 'rgba(245,158,11,0.15)', fg: '#f59e0b' },
    bitbucket: { bg: 'rgba(34,197,94,0.15)',  fg: '#22c55e' },
    gitea:     { bg: 'rgba(96,153,38,0.15)',  fg: '#84cc16' },
  };
  const { bg, fg } = map[provider] || { bg: C.border, fg: C.muted };
  return (
    <span style={{
      background: bg, color: fg,
      fontSize: 11, fontWeight: 600,
      padding: '2px 7px', borderRadius: 8,
      textTransform: 'capitalize',
    }}>
      {provider}
    </span>
  );
}

function ProductBreakdown({ rows }) {
  if (!rows || rows.length === 0) return (
    <div style={{ padding: '8px 0', color: C.muted, fontSize: 12, fontStyle: 'italic' }}>
      No product breakdown available.
    </div>
  );
  return (
    <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12, marginTop: 4 }}>
      <thead>
        <tr style={{ borderBottom: `1px solid ${C.border}` }}>
          {['Product', 'Files Covered', '% of Repo'].map(h => (
            <th key={h} style={{
              textAlign: h === 'Product' ? 'left' : 'right',
              padding: '4px 10px',
              color: C.textSecondary, fontWeight: 500,
              fontSize: 11, textTransform: 'uppercase', letterSpacing: '0.04em',
            }}>{h}</th>
          ))}
        </tr>
      </thead>
      <tbody>
        {rows.map((r, i) => (
          <tr key={i} style={{
            borderBottom: `1px solid ${C.border}`,
            background: i % 2 === 0 ? 'transparent' : 'rgba(255,255,255,0.02)',
          }}>
            <td style={{ padding: '4px 10px', color: C.textPrimary }}>{r.product_name}</td>
            <td style={{ padding: '4px 10px', textAlign: 'right', color: C.textSecondary }}>{r.files_covered}</td>
            <td style={{ padding: '4px 10px', textAlign: 'right' }}>
              <CoverageBadge pct={r.pct_of_repo} />
            </td>
          </tr>
        ))}
      </tbody>
    </table>
  );
}

// ── Coverage file-list drill-down ─────────────────────────────────────────────

// Rendering tens of thousands of rows freezes the table, so cap the DOM and
// tell the user to narrow with the filter instead.
const FILE_LIST_RENDER_CAP = 500;

function CountLink({ value, color, title, onClick }) {
  return (
    <button
      onClick={onClick}
      title={title}
      style={{
        background: 'none', border: 'none', padding: 0, cursor: 'pointer',
        color, fontSize: 13, fontWeight: 600,
        textDecoration: 'underline', textDecorationStyle: 'dotted',
        textUnderlineOffset: 3,
      }}
    >
      {value.toLocaleString()}
    </button>
  );
}

/**
 * Modal listing the mapped ('covered') or unmapped ('uncovered') files of a repo.
 *
 * The lists live on GET /repositories/<id>/coverage, written by the nightly
 * coverage job — so they reflect the last job run, not the live repo.
 */
function CoverageFilesModal({ repo, kind, onClose, navigate }) {
  const [loading, setLoading] = useState(true);
  const [error, setError]     = useState('');
  const [files, setFiles]     = useState(null);   // null → not available yet
  const [query, setQuery]     = useState('');
  const [copied, setCopied]   = useState(false);

  const isCovered = kind === 'covered';

  useEffect(() => {
    let alive = true;
    setLoading(true);
    setError('');
    axios.get(`/repositories/${repo.repo_id}/coverage`)
      .then(res => {
        if (!alive) return;
        const list = isCovered ? res.data.covered_file_list : res.data.uncovered_file_list;
        setFiles(Array.isArray(list) ? list : null);
      })
      .catch(e => {
        if (!alive) return;
        setError(e.response?.data?.error || 'Could not load the file list.');
      })
      .finally(() => { if (alive) setLoading(false); });
    return () => { alive = false; };
  }, [repo.repo_id, isCovered]);

  useEffect(() => {
    const onKey = e => { if (e.key === 'Escape') onClose(); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose]);

  const q        = query.trim().toLowerCase();
  const filtered = q ? (files || []).filter(f => f.toLowerCase().includes(q)) : (files || []);
  const shown    = filtered.slice(0, FILE_LIST_RENDER_CAP);

  async function handleCopy() {
    try {
      await navigator.clipboard.writeText(filtered.join('\n'));
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {
      setError('Could not copy to the clipboard in this browser — use Download instead.');
    }
  }

  function handleDownload() {
    const blob = new Blob([filtered.join('\n')], { type: 'text/plain' });
    const url  = URL.createObjectURL(blob);
    const a    = document.createElement('a');
    a.href     = url;
    a.download = `${repo.repo_name.replace(/[^\w.-]+/g, '_')}-${kind}-files.txt`;
    a.click();
    URL.revokeObjectURL(url);
  }

  return (
    <div
      onClick={onClose}
      style={{
        position: 'fixed', inset: 0, zIndex: 1000,
        background: 'rgba(0,0,0,0.7)',
        display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 20,
      }}
    >
      <div
        onClick={e => e.stopPropagation()}
        style={{
          background: C.surface, border: `1px solid ${C.border}`, borderRadius: 10,
          width: '100%', maxWidth: 860, maxHeight: '80vh',
          display: 'flex', flexDirection: 'column', overflow: 'hidden',
        }}
      >
        {/* Header */}
        <div style={{
          padding: '14px 18px', borderBottom: `1px solid ${C.border}`,
          display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12,
        }}>
          <div style={{ minWidth: 0 }}>
            <div style={{ fontSize: 14, fontWeight: 600, color: C.textPrimary }}>
              {isCovered ? 'Mapped files' : 'Unmapped files'}
              <span style={{ color: isCovered ? C.success : C.danger, marginLeft: 8 }}>
                {(files?.length ?? 0).toLocaleString()}
              </span>
            </div>
            <div style={{
              fontSize: 12, color: C.textSecondary, marginTop: 2,
              overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
            }}>
              {repo.repo_name}
              {repo.stale && (
                <span style={{ color: C.warning, marginLeft: 8 }}>
                  · features changed since this report — re-run the coverage job
                </span>
              )}
              {repo.truncated && (
                <span style={{ color: C.warning, marginLeft: 8 }}>
                  · file tree was truncated by the SCM provider
                </span>
              )}
            </div>
          </div>
          <button
            onClick={onClose}
            style={{ background: 'none', border: 'none', color: C.textSecondary,
                     cursor: 'pointer', fontSize: 20, lineHeight: 1, padding: '0 4px' }}
            title="Close (Esc)"
          >
            ×
          </button>
        </div>

        {/* Toolbar */}
        {files !== null && files.length > 0 && (
          <div style={{
            padding: '10px 18px', borderBottom: `1px solid ${C.border}`,
            display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap',
          }}>
            <input
              value={query}
              onChange={e => setQuery(e.target.value)}
              placeholder="Filter by path…"
              style={{
                flex: 1, minWidth: 200, background: C.elevated,
                border: `1px solid ${C.border}`, borderRadius: 6,
                color: C.textPrimary, fontSize: 13, padding: '6px 10px',
              }}
            />
            <span style={{ fontSize: 12, color: C.muted, whiteSpace: 'nowrap' }}>
              {filtered.length.toLocaleString()} match{filtered.length !== 1 ? 'es' : ''}
            </span>
            <button
              onClick={handleCopy}
              style={{ background: 'none', border: `1px solid ${C.border}`, borderRadius: 6,
                       color: C.textSecondary, cursor: 'pointer', fontSize: 11, padding: '4px 10px' }}
            >
              {copied ? 'Copied' : 'Copy'}
            </button>
            <button
              onClick={handleDownload}
              style={{ background: 'none', border: `1px solid ${C.border}`, borderRadius: 6,
                       color: C.textSecondary, cursor: 'pointer', fontSize: 11, padding: '4px 10px' }}
            >
              Download
            </button>
          </div>
        )}

        {/* Body */}
        <div style={{ flex: 1, overflow: 'auto', padding: '8px 0' }}>
          {loading && (
            <div style={{ padding: '20px 18px', color: C.muted, fontSize: 13 }}>Loading…</div>
          )}

          {!loading && error && (
            <div style={{ padding: '16px 18px' }}><ErrorBanner message={error} /></div>
          )}

          {!loading && !error && files === null && (
            <div style={{ padding: '20px 18px', color: C.textSecondary, fontSize: 13, lineHeight: 1.7 }}>
              {isCovered
                ? 'The mapped-file list has not been generated yet — it is written the next time the coverage report runs.'
                : 'No unmapped-file list stored for this repository yet.'}
              <br />
              Go to <button
                onClick={() => navigate('/jobs')}
                style={{ background: 'none', border: 'none', color: C.accent,
                         cursor: 'pointer', fontSize: 13, padding: 0 }}
              >Jobs</button> and run the “Repository Coverage Report” job.
            </div>
          )}

          {!loading && !error && files !== null && files.length === 0 && (
            <div style={{ padding: '20px 18px', color: C.muted, fontSize: 13 }}>
              {isCovered
                ? 'No files in this repository are mapped to a feature.'
                : 'Every file in this repository is mapped to a feature. 🎉'}
            </div>
          )}

          {!loading && !error && shown.map(f => (
            <div
              key={f}
              style={{
                display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                gap: 12, padding: '5px 18px', fontSize: 12.5,
                fontFamily: 'monospace', color: C.textPrimary,
              }}
            >
              <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}
                    title={f}>
                {f}
              </span>
              <button
                onClick={() => navigate(
                  `/features/file-mapping?repo=${encodeURIComponent(repo.repo_name)}` +
                  `&file_path=${encodeURIComponent(f)}`
                )}
                style={{ background: 'none', border: 'none', color: C.accent, cursor: 'pointer',
                         fontSize: 11, padding: 0, whiteSpace: 'nowrap',
                         fontFamily: 'inherit', flexShrink: 0 }}
                title={isCovered
                  ? 'Open the file-mapping tool to see which features map to this file'
                  : 'Open the file-mapping tool to map this file to features'}
              >
                {isCovered ? 'view mapping →' : 'map file →'}
              </button>
            </div>
          ))}

          {!loading && !error && filtered.length > shown.length && (
            <div style={{ padding: '10px 18px', color: C.muted, fontSize: 12 }}>
              Showing the first {FILE_LIST_RENDER_CAP.toLocaleString()} of{' '}
              {filtered.length.toLocaleString()} — use the filter above, or download the full list.
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

function CoverageTable({ repos, navigate }) {
  const [expandedId, setExpandedId] = useState(null);
  const [filesModal, setFilesModal] = useState(null);   // { repo, kind }

  const TH = { padding: '10px 14px', color: C.textSecondary, fontWeight: 500,
               fontSize: 12, textTransform: 'uppercase', letterSpacing: '0.05em', whiteSpace: 'nowrap' };

  return (
    <div style={{ overflowX: 'auto' }}>
      <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
        <thead>
          <tr style={{ borderBottom: `1px solid ${C.border}` }}>
            <th style={{ ...TH, textAlign: 'left' }}>Repository</th>
            <th style={{ ...TH, textAlign: 'left' }}>Provider</th>
            <th style={{ ...TH, textAlign: 'right' }}>Total Files</th>
            <th style={{ ...TH, textAlign: 'right' }}>Covered</th>
            <th style={{ ...TH, textAlign: 'right' }}>Uncovered</th>
            <th style={{ ...TH, textAlign: 'right' }}>Coverage</th>
            <th style={{ ...TH, textAlign: 'center' }}>Breakdown</th>
          </tr>
        </thead>
        <tbody>
          {repos.map((repo, i) => {
            const isOpen   = expandedId === repo.repo_id;
            const hasError = Boolean(repo.error);
            return (
              <React.Fragment key={repo.repo_id}>
                <tr style={{
                  background: isOpen ? 'rgba(99,102,241,0.06)'
                    : i % 2 === 0 ? 'transparent' : 'rgba(255,255,255,0.02)',
                  borderBottom: isOpen ? 'none' : `1px solid ${C.border}`,
                }}>
                  <td style={{ padding: '10px 14px', color: C.textPrimary, fontWeight: 500,
                               maxWidth: 240, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                    {repo.repo_name}
                    {repo.stale && (
                      <span
                        title="Coverage data is outdated — features were modified since this report was generated"
                        style={{
                          marginLeft: 6, fontSize: 10, fontWeight: 600,
                          color: C.warning,
                          background: 'rgba(245,158,11,0.15)',
                          padding: '1px 5px', borderRadius: 4,
                        }}
                      >
                        stale
                      </span>
                    )}
                    {repo.truncated && (
                      <span title="File tree was truncated by SCM provider"
                            style={{ marginLeft: 6, fontSize: 10, color: C.warning }}>&#9888; truncated</span>
                    )}
                  </td>
                  <td style={{ padding: '10px 14px' }}>
                    <ProviderBadge provider={repo.provider} />
                  </td>
                  <td style={{ padding: '10px 14px', textAlign: 'right', color: C.textSecondary }}>
                    {hasError ? '—' : repo.total_files.toLocaleString()}
                  </td>
                  <td style={{ padding: '10px 14px', textAlign: 'right', color: C.success }}>
                    {hasError ? '—' : repo.covered_files > 0
                      ? <CountLink
                          value={repo.covered_files}
                          color={C.success}
                          title="Show the mapped files"
                          onClick={() => setFilesModal({ repo, kind: 'covered' })}
                        />
                      : repo.covered_files.toLocaleString()}
                  </td>
                  <td style={{ padding: '10px 14px', textAlign: 'right',
                               color: hasError ? C.muted : repo.uncovered_files > 0 ? C.danger : C.muted }}>
                    {hasError ? '—' : repo.uncovered_files > 0
                      ? <CountLink
                          value={repo.uncovered_files}
                          color={C.danger}
                          title="Show the files not covered by any feature glob"
                          onClick={() => setFilesModal({ repo, kind: 'uncovered' })}
                        />
                      : repo.uncovered_files.toLocaleString()}
                  </td>
                  <td style={{ padding: '10px 14px', textAlign: 'right' }}>
                    {hasError
                      ? <span style={{ color: C.danger, fontSize: 11 }} title={repo.error}>Error</span>
                      : <CoverageBadge pct={repo.pct_covered} />}
                  </td>
                  <td style={{ padding: '10px 14px', textAlign: 'center' }}>
                    {!hasError && repo.by_product?.length > 0 && (
                      <button
                        onClick={() => setExpandedId(isOpen ? null : repo.repo_id)}
                        style={{
                          background: isOpen ? C.accent : 'transparent',
                          border: `1px solid ${isOpen ? C.accent : C.border}`,
                          borderRadius: 6, color: isOpen ? '#fff' : C.textSecondary,
                          cursor: 'pointer', fontSize: 11, padding: '3px 10px',
                          fontWeight: isOpen ? 600 : 400,
                        }}
                      >
                        {isOpen ? 'Hide' : 'Show'}
                      </button>
                    )}
                  </td>
                </tr>
                {isOpen && (
                  <tr>
                    <td colSpan={7} style={{
                      padding: '12px 24px 16px',
                      background: '#1a2535',
                      borderBottom: `1px solid ${C.border}`,
                    }}>
                      <ProductBreakdown rows={repo.by_product} />
                    </td>
                  </tr>
                )}
              </React.Fragment>
            );
          })}
        </tbody>
      </table>

      {filesModal && (
        <CoverageFilesModal
          repo={filesModal.repo}
          kind={filesModal.kind}
          navigate={navigate}
          onClose={() => setFilesModal(null)}
        />
      )}
    </div>
  );
}

// ── Repo Mapping breakdown table ──────────────────────────────────────────────
function MappingTable({ rows }) {
  return (
    <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
      <thead>
        <tr style={{ borderBottom: `1px solid ${C.border}` }}>
          {['Product', 'Total Features', 'Mapped', 'Unmapped', 'Coverage'].map(h => (
            <th key={h} style={{
              textAlign: h === 'Product' ? 'left' : 'right',
              padding: '8px 12px',
              color: C.textSecondary,
              fontWeight: 500,
              fontSize: 12,
              textTransform: 'uppercase',
              letterSpacing: '0.05em',
            }}>{h}</th>
          ))}
        </tr>
      </thead>
      <tbody>
        {rows.map((r, i) => {
          const pct = r.total > 0 ? Math.round((r.mapped / r.total) * 100) : 0;
          const tone = pct >= 80 ? C.success : pct >= 50 ? C.warning : C.danger;
          return (
            <tr key={i} style={{
              borderBottom: `1px solid ${C.border}`,
              background: i % 2 === 0 ? 'transparent' : 'rgba(255,255,255,0.02)',
            }}>
              <td style={{ padding: '8px 12px', color: C.textPrimary }}>{r.product_name}</td>
              <td style={{ padding: '8px 12px', textAlign: 'right', color: C.textSecondary }}>{r.total}</td>
              <td style={{ padding: '8px 12px', textAlign: 'right', color: C.success }}>{r.mapped}</td>
              <td style={{ padding: '8px 12px', textAlign: 'right', color: r.total - r.mapped > 0 ? C.danger : C.muted }}>
                {r.total - r.mapped}
              </td>
              <td style={{ padding: '8px 12px', textAlign: 'right' }}>
                <span style={{
                  color: tone,
                  fontWeight: 600,
                  background: `${tone}18`,
                  padding: '2px 8px',
                  borderRadius: 12,
                  fontSize: 12,
                }}>{pct}%</span>
              </td>
            </tr>
          );
        })}
      </tbody>
    </table>
  );
}

// ── Main component ────────────────────────────────────────────────────────────
export default function Dashboard() {
  const [data, setData]     = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError]   = useState(null);
  const navigate = useNavigate();

  const load = useCallback(() => {
    setLoading(true);
    setError(null);
    axios.get('/dashboard/summary')
      .then(res => setData(res.data))
      .catch(err => setError(err.response?.data?.error || err.message))
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => { load(); }, [load]);

  // ── derived ─────────────────────────────────────────────────────────────────
  const productBarData = data
    ? [
        ...data.products,
        ...(data.unassigned_features > 0
          ? [{ name: 'Unassigned', feature_count: data.unassigned_features, pm_names: null }]
          : []),
      ]
    : [];

  const { total_features = 0, mapped = 0, unmapped = 0, pct_mapped = 0 } =
    data?.repo_mapping ?? {};

  const coverageRepos = data?.coverage?.repos ?? [];
  const coverageLastGenerated = data?.coverage?.last_generated_at ?? null;
  const successRepos = coverageRepos.filter(r => !r.error);
  const staleRepos = coverageRepos.filter(r => r.stale);
  const avgCoverage = successRepos.length > 0
    ? Math.round(successRepos.reduce((s, r) => s + r.pct_covered, 0) / successRepos.length * 10) / 10
    : null;

  const pieData = [
    { name: 'Mapped',   value: mapped,   fill: C.success, pct: pct_mapped },
    { name: 'Unmapped', value: unmapped,  fill: C.danger,  pct: +(100 - pct_mapped).toFixed(1) },
  ].filter(d => d.value > 0);

  // ── render ──────────────────────────────────────────────────────────────────
  return (
    <div style={{ padding: '24px 28px', maxWidth: 1200 }}>
      {/* Header */}
      <div style={{ marginBottom: 28 }}>
        <h1 style={{ fontSize: 22, fontWeight: 700, color: C.textPrimary }}>Dashboard</h1>
        <p style={{ color: C.textSecondary, marginTop: 4, fontSize: 13 }}>
          Product feature coverage and repository mapping overview
        </p>
      </div>

      {error && <ErrorBanner message={error} />}
      {loading && <Spinner />}

      {!loading && data && (
        <>
          {/* ── Summary stats ── */}
          <div style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fit, minmax(160px, 1fr))',
            gap: 14,
            marginBottom: 32,
          }}>
            <StatCard label="Products"       value={data.products.length} />
            <StatCard label="Total Features" value={total_features} />
            <StatCard label="Repo Mapped"    value={mapped}   tone="success" sub={`${pct_mapped}% of features`} />
            <StatCard label="Not Mapped"     value={unmapped} tone={unmapped > 0 ? 'warning' : 'success'} />
            {avgCoverage !== null && (
              <StatCard
                label="Avg File Coverage"
                value={`${avgCoverage}%`}
                sub={`across ${successRepos.length} repo${successRepos.length !== 1 ? 's' : ''}`}
                tone={avgCoverage >= 80 ? 'success' : avgCoverage >= 50 ? 'warning' : 'danger'}
              />
            )}
          </div>

          {/* ── Chart row ── */}
          <div style={{
            display: 'grid',
            gridTemplateColumns: '1fr 340px',
            gap: 20,
            marginBottom: 32,
          }}>
            {/* Bar chart: Products vs Features */}
            <div style={{
              background: C.surface,
              border: `1px solid ${C.border}`,
              borderRadius: 10,
              padding: '20px 20px 12px',
            }}>
              <div style={{ marginBottom: 16 }}>
                <div style={{ fontSize: 14, fontWeight: 600, color: C.textPrimary }}>
                  Features per Product
                </div>
                <div style={{ fontSize: 12, color: C.textSecondary, marginTop: 3 }}>
                  Hover a bar to see assigned PMs
                </div>
              </div>
              {productBarData.length === 0 ? (
                <div style={{ color: C.muted, fontSize: 13, padding: '24px 0' }}>No products found.</div>
              ) : (
                <ResponsiveContainer width="100%" height={380}>
                  <BarChart
                    data={productBarData}
                    margin={{ top: 4, right: 8, left: -16, bottom: 80 }}
                    barCategoryGap="30%"
                  >
                    <CartesianGrid vertical={false} stroke={C.border} strokeDasharray="3 3" />
                    <XAxis
                      dataKey="name"
                      tick={<TruncatedTick />}
                      axisLine={{ stroke: C.border }}
                      tickLine={false}
                      interval={0}
                    />
                    <YAxis
                      allowDecimals={false}
                      tick={{ fill: C.textSecondary, fontSize: 11 }}
                      axisLine={false}
                      tickLine={false}
                    />
                    <Tooltip content={<ProductTooltip />} cursor={{ fill: 'rgba(99,102,241,0.06)' }} />
                    <Bar dataKey="feature_count" radius={[4, 4, 0, 0]}>
                      {productBarData.map((entry, i) => (
                        <Cell
                          key={i}
                          fill={entry.name === 'Unassigned' ? C.muted : C.accent}
                        />
                      ))}
                    </Bar>
                  </BarChart>
                </ResponsiveContainer>
              )}
            </div>

            {/* Donut chart: Repo mapping */}
            <div style={{
              background: C.surface,
              border: `1px solid ${C.border}`,
              borderRadius: 10,
              padding: '20px 20px 12px',
              display: 'flex',
              flexDirection: 'column',
            }}>
              <div style={{ marginBottom: 8 }}>
                <div style={{ fontSize: 14, fontWeight: 600, color: C.textPrimary }}>
                  Repo Mapping Coverage
                </div>
                <div style={{ fontSize: 12, color: C.textSecondary, marginTop: 3 }}>
                  Features resolved to a repository
                </div>
              </div>

              {/* Big percentage label */}
              <div style={{ textAlign: 'center', margin: '8px 0 0' }}>
                <span style={{ fontSize: 40, fontWeight: 800, color: pct_mapped >= 80 ? C.success : pct_mapped >= 50 ? C.warning : C.danger }}>
                  {pct_mapped}%
                </span>
                <div style={{ fontSize: 12, color: C.textSecondary }}>mapped</div>
              </div>

              {pieData.length > 0 ? (
                <ResponsiveContainer width="100%" height={200}>
                  <PieChart>
                    <Pie
                      data={pieData}
                      cx="50%"
                      cy="50%"
                      innerRadius={55}
                      outerRadius={80}
                      dataKey="value"
                      strokeWidth={0}
                    >
                      {pieData.map((entry, i) => (
                        <Cell key={i} fill={entry.fill} />
                      ))}
                    </Pie>
                    <Tooltip content={<MappingTooltip />} />
                    <Legend
                      iconType="circle"
                      iconSize={8}
                      formatter={(v, entry) => (
                        <span style={{ color: C.textSecondary, fontSize: 12 }}>
                          {v} ({entry.payload.value})
                        </span>
                      )}
                    />
                  </PieChart>
                </ResponsiveContainer>
              ) : (
                <div style={{ color: C.muted, fontSize: 13, padding: '24px 0', textAlign: 'center' }}>
                  No features yet.
                </div>
              )}
            </div>
          </div>

          {/* ── Breakdown table ── */}
          <div style={{
            background: C.surface,
            border: `1px solid ${C.border}`,
            borderRadius: 10,
            overflow: 'hidden',
          }}>
            <div style={{ padding: '16px 20px', borderBottom: `1px solid ${C.border}` }}>
              <div style={{ fontSize: 14, fontWeight: 600, color: C.textPrimary }}>
                Repo Mapping by Product
              </div>
              <div style={{ fontSize: 12, color: C.textSecondary, marginTop: 3 }}>
                Per-product breakdown of features resolved to a repository
              </div>
            </div>
            <div style={{ overflowX: 'auto' }}>
              {data.mapping_by_product?.length > 0
                ? <MappingTable rows={data.mapping_by_product} />
                : <div style={{ padding: 20, color: C.muted, fontSize: 13 }}>No data.</div>
              }
            </div>
          </div>

          {/* ── Repository Coverage section ── */}
          <div style={{
            background: C.surface,
            border: `1px solid ${C.border}`,
            borderRadius: 10,
            overflow: 'hidden',
            marginTop: 20,
          }}>
            <div style={{
              padding: '16px 20px',
              borderBottom: `1px solid ${C.border}`,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              flexWrap: 'wrap',
              gap: 8,
            }}>
              <div>
                <div style={{ fontSize: 14, fontWeight: 600, color: C.textPrimary }}>
                  Repository File Coverage
                </div>
                <div style={{ fontSize: 12, color: C.textSecondary, marginTop: 3 }}>
                  Pre-generated daily — percentage of files covered by mapped feature globs.
                  Vendored dependencies and build output (vendor, node_modules, target, dist, …)
                  are excluded.
                  {coverageLastGenerated && (
                    <span style={{ marginLeft: 8, color: C.muted }}>
                      · Last generated {new Date(coverageLastGenerated).toLocaleString()}
                    </span>
                  )}
                </div>
              </div>
              <button
                onClick={() => navigate('/jobs')}
                style={{
                  background: 'none',
                  border: `1px solid ${C.border}`,
                  borderRadius: 6,
                  color: C.textSecondary,
                  cursor: 'pointer',
                  fontSize: 11,
                  padding: '4px 12px',
                }}
                title="Go to Jobs page to trigger the coverage report manually"
              >
                Run job manually →
              </button>
            </div>

            {coverageRepos.length === 0 ? (
              <div style={{ padding: '24px 20px', color: C.muted, fontSize: 13 }}>
                No coverage report generated yet.
                Go to <button
                  onClick={() => navigate('/jobs')}
                  style={{ background: 'none', border: 'none', color: C.accent,
                           cursor: 'pointer', fontSize: 13, padding: 0 }}
                >Jobs</button> and trigger the "Repository Coverage Report" job to generate data.
              </div>
            ) : (
              <>
                {staleRepos.length > 0 && (
                  <div style={{ padding: '0 20px' }}>
                    <StaleBanner staleRepos={staleRepos} navigate={navigate} />
                  </div>
                )}
                <CoverageTable repos={coverageRepos} navigate={navigate} />
                <div style={{
                  padding: '10px 20px',
                  borderTop: `1px solid ${C.border}`,
                  fontSize: 11,
                  color: C.muted,
                  display: 'flex',
                  gap: 16,
                  flexWrap: 'wrap',
                }}>
                  <span><span style={{ color: C.success, fontWeight: 600 }}>■</span> ≥ 80%</span>
                  <span><span style={{ color: C.warning, fontWeight: 600 }}>■</span> 50–79%</span>
                  <span><span style={{ color: C.danger,  fontWeight: 600 }}>■</span> &lt; 50%</span>
                </div>
              </>
            )}
          </div>

          {/* Refresh link */}
          <div style={{ marginTop: 16, textAlign: 'right' }}>
            <button
              onClick={load}
              style={{
                background: 'none',
                border: 'none',
                color: C.accent,
                cursor: 'pointer',
                fontSize: 12,
                padding: 0,
              }}
            >
              Refresh
            </button>
          </div>
        </>
      )}
    </div>
  );
}
